import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import Undo from "@mui/icons-material/Undo";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import {
  GridActionsCellItem,
  GridRenderCellParams,
  useGridApiContext,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { StatusChip } from "components/StatusChip";
import { Issue, IssueLevel, ShippingDocumentKind } from "generated-graphql/graphql";
import pluralize from "pluralize";
import { useCallback, useMemo } from "react";

import { WasteClassificationChip } from "../WasteClassificationChip";
import { attachedDocumentNoun, diffFieldLabel, formatDiffValue } from "./reconciliationDisplay";
import { AmountCell, ContainerCell } from "./ShipmentContentsCells";
import {
  isDisplayLine,
  orderedForDisplay,
  streamKeyFor,
  streamKeyOf,
  StreamRollup,
  UNCHARACTERIZED_NAME,
} from "./shipmentContentsRow";
import { totalsByUnitFor } from "./shipmentDisplay";
import { FieldDiff } from "./StreamComparison";
import { DisplayLine } from "./useStagedShipmentLines";

/**
 * The hidden column the grid groups on. Streams group by id rather than name:
 * the grid groups on a cell's value, and two streams may legitimately share a
 * name — the id is the identity.
 */
const STREAM_GROUPING_FIELD = "streamKey";

/**
 * Left padding that lines a container up with the stream name above it, leaving
 * the group row's chevron hanging outside the text column — the way the frames
 * set it. Matches the small IconButton plus the stack gap beside it.
 */
const CONTAINER_INDENT = "38px";

/**
 * How many columns a container row may span: the grouping column it starts in,
 * plus every stream-describing column to its right, which are empty on that row
 * and so can be borrowed. Stops short of Amount, which the container does fill.
 *
 * Read off the column list rather than counted by hand. Discrepancies arrives
 * conditionally, between the stream facts and Amount, so a fixed count stopped a
 * column short whenever a manifest was attached — leaving a dead cell between a
 * container's label and its figure — and would drift again the next time a
 * stream column was added.
 */
function containerRowSpan(columns: CustomGridColDef<DisplayLine>[]): number {
  const amountIndex = columns.findIndex((column) => column.field === "amount");
  // The hidden grouping column sits at index 0 and the visible grouping colDef
  // stands in for it, so Amount's index is exactly the span that stops short
  // of it.
  return amountIndex === -1 ? 1 : amountIndex;
}

type RowActions = {
  onEditLine: (line: DisplayLine) => void;
  onRemoveLine: (line: DisplayLine) => void;
  onRestoreLine: (line: DisplayLine) => void;
};

/**
 * The column the grid groups on, which is the one that holds two different
 * things: the stream name on a group row, and the container's own identity on
 * the rows beneath it. Putting the container there rather than in its own
 * column is what makes the nesting read as nesting — the indent is the
 * relationship.
 */
function buildGroupingColDef({
  rollups,
  shipped,
  issuesForContainer,
  onOpenContainer,
  containerSpan,
}: {
  rollups: Map<string, StreamRollup>;
  shipped: boolean;
  issuesForContainer: (containerId: string) => Issue[];
  onOpenContainer: (containerId: string) => void;
  containerSpan: number;
}) {
  return {
    headerName: "Waste Stream",
    // The widest share, for the two things above.
    flex: 2,
    minWidth: 240,
    // MUI's own "(N)" would sit after the name and duplicate the count the
    // group cell renders itself, which is placed to survive truncation.
    hideDescendantCount: true,
    // The one column with an order worth offering: it reorders the stream
    // groups, alphabetically, because the key it sorts on leads with the name.
    // Every other column describes the stream too, so sorting by one would
    // reorder containers by a value they all share.
    sortable: true,
    /*
      A container's label and its facts need room, and the columns to the right
      of this one describe the stream rather than the container — so on a
      container row they are empty cells this can borrow. Spanning them stops
      "Drum, barrel, or keg (fiberboard or plastic) · 2 gals on site ·
      Warehouse" being cut off at a column edge for no reason. The span stops
      short of Amount, which the container does fill.
    */
    colSpan: (_value: unknown, row: unknown) => (isDisplayLine(row) ? containerSpan : 1),
    renderCell: (params: GridRenderCellParams) => (
      <StreamOrContainerCell
        params={params}
        rollups={rollups}
        shipped={shipped}
        issuesForContainer={issuesForContainer}
        onOpenContainer={onOpenContainer}
      />
    ),
  };
}

/**
 * A shipment's containers, grouped by the waste stream they belong to.
 *
 * The stream is the unit the vendor sees — the manifest request rolls up to it
 * — so it is the group row here, carrying the rollup. Its containers are the
 * rows underneath, which is the grain everything actionable lives at: a
 * container links to its own page, ships its own amount, and carries its own
 * problems. Grouping is what lets both be true at once.
 */
/**
 * The column set and the grouping column, both memoized.
 *
 * MUI re-derives the grouping column and re-hydrates the whole column set
 * whenever either object's identity changes, which inline made it every render
 * of the page. They live together because the grouping column measures itself
 * against the columns beside it (`containerRowSpan`), so a build of one that
 * did not see the other would span the wrong number of cells.
 */
function useGridColumns({
  editable,
  contentLockReason,
  shipped,
  rollups,
  diffsByStream,
  documentKind,
  issuesForContainer,
  onEditLine,
  onRemoveLine,
  onRestoreLine,
  onOpenContainer,
}: {
  editable: boolean;
  contentLockReason?: string;
  shipped: boolean;
  rollups: Map<string, StreamRollup>;
  diffsByStream?: Map<string, FieldDiff[]>;
  documentKind?: ShippingDocumentKind | null;
  issuesForContainer: (containerId: string) => Issue[];
  onOpenContainer: (containerId: string) => void;
} & RowActions) {
  const columns = useMemo(
    () =>
      buildColumns({
        editable,
        contentLockReason,
        shipped,
        rollups,
        diffsByStream,
        documentKind,
        actions: { onEditLine, onRemoveLine, onRestoreLine },
      }),
    [
      editable,
      contentLockReason,
      shipped,
      rollups,
      diffsByStream,
      documentKind,
      onEditLine,
      onRemoveLine,
      onRestoreLine,
    ]
  );

  const groupingColDef = useMemo(
    () =>
      buildGroupingColDef({
        rollups,
        shipped,
        issuesForContainer,
        onOpenContainer,
        containerSpan: containerRowSpan(columns),
      }),
    [rollups, shipped, issuesForContainer, onOpenContainer, columns]
  );

  return { columns, groupingColDef };
}

export function ShipmentContentsGrid({
  lines,
  editable,
  contentLockReason,
  shipped,
  issues,
  diffsByStream,
  documentKind,
  onEditLine,
  onRemoveLine,
  onRestoreLine,
  onOpenContainer,
}: {
  /** Saved lines plus anything staged; see useStagedShipmentLines. */
  lines: DisplayLine[];
  editable: boolean;
  /**
   * Why the contents cannot be edited, when the shipment would otherwise be
   * editable — an attached manifest or bill of lading freezes them.
   */
  contentLockReason?: string;
  shipped: boolean;
  /** Standing issues for the shipment; per-container ones carry a modelId. */
  issues: Issue[];
  /**
   * The attached document's per-stream comparison, or undefined when nothing is
   * attached — which is what decides whether the column exists.
   *
   * Keyed on attachment rather than on the map being non-empty, for two
   * reasons: the page builds the map either way, so keying on its contents
   * would give every unlinked shipment a blank column implying a comparison had
   * been made and found nothing; and a document that agrees on every stream is
   * a real state with no diffs in it, where the empty column is the answer.
   */
  diffsByStream?: Map<string, FieldDiff[]>;
  /**
   * Which kind of paper the comparison column is reporting against, so its
   * tooltip names the actual document instead of always saying "manifest".
   */
  documentKind?: ShippingDocumentKind | null;
  /** Opens a container's drawer over this page. Not a RowActions member: that
   * shape is also the column builder's `actions`, which never opens one. */
  onOpenContainer: (containerId: string) => void;
} & RowActions) {
  const apiRef = useGridApiRef();
  const rows = useMemo(() => orderedForDisplay(lines), [lines]);
  const rollups = useMemo(() => buildRollups(lines, issues), [lines, issues]);
  const issuesForContainer = useCallback(
    (containerId: string) => issues.filter((issue) => issue.modelId === containerId),
    [issues]
  );

  const { columns, groupingColDef } = useGridColumns({
    editable,
    contentLockReason,
    shipped,
    rollups,
    diffsByStream,
    documentKind,
    issuesForContainer,
    onEditLine,
    onRemoveLine,
    onRestoreLine,
    onOpenContainer,
  });

  return (
    <DataGrid<DisplayLine>
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      getRowId={(row) => row.key}
      autoHeight
      hideFooter
      disableColumnMenu
      disableRowSelectionOnClick
      disableRowGrouping={false}
      rowGroupingModel={[STREAM_GROUPING_FIELD]}
      // Grouping is here for the rollup, not to hide anything — a shipment is
      // opened to find out what is on it, so every stream starts expanded.
      defaultGroupingExpansionDepth={-1}
      columnVisibilityModel={{ [STREAM_GROUPING_FIELD]: false }}
      groupingColDef={groupingColDef}
      // The whole heading is the control, not just its chevron: the row is a
      // disclosure and a 20px target inside it is a worse version of the same
      // affordance. Leaf rows have nothing to toggle, so they ignore the click.
      onRowClick={(params) => {
        const node = apiRef.current?.getRowNode(params.id);
        if (node?.type === "group") {
          apiRef.current?.setRowChildrenExpansion(params.id, !node.childrenExpanded);
        }
      }}
      /*
        Classed from the row itself rather than from a MUI class: the grid marks
        group rows in its internal state, not with a `--group` class on the row
        element, so keying off one silently matched every row and tinted the
        stream heading along with its containers.
      */
      getRowClassName={(params) =>
        [
          isDisplayLine(params.row) ? "shipment-row--container" : "shipment-row--stream",
          params.row?.pending === "removed" ? "shipment-line--removed" : "",
        ]
          .filter(Boolean)
          .join(" ")
      }
      sx={{
        // Containers are tinted and the stream heading is not, per the frames:
        // the heading is the row of the table, and its containers are a nested
        // block underneath it.
        "& .shipment-row--container": { backgroundColor: "grey.50" },
        // Only the heading toggles, so only the heading says so. The wrapper
        // puts a pointer on every row by default.
        "& .shipment-row--container:hover": { cursor: "default" },
        "& .shipment-line--removed": { opacity: 0.5 },
      }}
      rowHeight={52}
    />
  );
}

function buildRollups(lines: DisplayLine[], issues: Issue[]): Map<string, StreamRollup> {
  const byKey = new Map<string, DisplayLine[]>();
  for (const line of lines) {
    const key = streamKeyOf(line);
    byKey.set(key, [...(byKey.get(key) ?? []), line]);
  }

  const rollups = new Map<string, StreamRollup>();
  for (const [key, groupLines] of byKey) {
    // Figures describe what will actually ship. A container staged for removal
    // stays visible, struck through, so Discard means something — but counting
    // it would report a total the shipment is not going to carry, directly
    // above rows that say otherwise. Its issues drop out for the same reason: a
    // heading must not report a blocker the shipment is no longer carrying.
    const counted = groupLines.filter((line) => line.pending !== "removed");
    const groupIssues = counted.flatMap((line) =>
      issues.filter((issue) => issue.modelId === line.container.id)
    );
    // Union the codes rather than trusting the first line's, and take the
    // first DOT/profile that is set — the same reading `groupLinesByStream`
    // uses, so a stream whose snapshot changed between two lines still shows
    // everything it has been classified as rather than silently picking one.
    const codes = new Set<string>();
    for (const line of groupLines) {
      for (const code of [...line.federalWasteCodes, ...line.stateWasteCodes]) {
        codes.add(code);
      }
    }

    rollups.set(key, {
      name: groupLines[0]?.wasteStream?.name ?? UNCHARACTERIZED_NAME,
      streamId: groupLines[0]?.wasteStream?.id ?? null,
      classification: groupLines[0]?.wasteStream?.classification ?? null,
      containerCount: counted.length,
      totals: totalsByUnitFor(counted),
      issueCount: groupIssues.length,
      blocked: groupIssues.some((issue) => issue.level === IssueLevel.Critical),
      wasteCodes: [...codes].join(", "),
      dotDescription: groupLines.find((line) => line.dotDescription)?.dotDescription ?? null,
      profileNumber: groupLines.find((line) => line.profileNumber)?.profileNumber ?? null,
    });
  }
  return rollups;
}

/**
 * The group row's cell: the stream, what it holds, and whether anything about
 * it is wrong. MUI calls the grouping column's renderer for leaf rows too,
 * where the row's own columns already say everything.
 *
 * The expand toggle is rendered here rather than inherited: overriding
 * `renderCell` on the grouping column replaces MUI's default cell wholesale,
 * and the toggle lives inside it — without this the groups cannot be collapsed
 * at all.
 */
function StreamOrContainerCell({
  params,
  rollups,
  shipped,
  issuesForContainer,
  onOpenContainer,
}: {
  params: GridRenderCellParams;
  rollups: Map<string, StreamRollup>;
  shipped: boolean;
  issuesForContainer: (containerId: string) => Issue[];
  onOpenContainer: (containerId: string) => void;
}) {
  if (params.rowNode.type !== "group") {
    return (
      <Box sx={{ pl: CONTAINER_INDENT, minWidth: 0, width: "100%" }}>
        <ContainerCell
          params={params}
          shipped={shipped}
          issuesForContainer={issuesForContainer}
          onOpenContainer={onOpenContainer}
        />
      </Box>
    );
  }
  return <StreamGroupCell params={params} rollups={rollups} />;
}

/*
 * The name is the only part of this row allowed to give up room. Everything
 * after it is short, fixed, and the reason the row is worth reading — so a long
 * stream name takes the ellipsis itself rather than pushing the count and the
 * chips out of the cell.
 */
function StreamGroupCell({
  params,
  rollups,
}: {
  params: GridRenderCellParams;
  rollups: Map<string, StreamRollup>;
}) {
  const apiRef = useGridApiContext();
  const { rowNode } = params;
  if (rowNode.type !== "group") {
    return null;
  }
  const rollup = rollups.get(String(rowNode.groupingKey));
  if (!rollup) {
    return null;
  }
  const expanded = rowNode.childrenExpanded ?? false;
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, width: "100%" }}>
      {/* Kept for keyboard reach and as the visible affordance, but the row
          around it toggles too — so this must not toggle a second time and
          cancel itself out. */}
      <IconButton
        size="small"
        sx={{ flexShrink: 0 }}
        aria-label={`${expanded ? "Collapse" : "Expand"} ${rollup.name}`}
        onClick={(e) => {
          e.stopPropagation();
          apiRef.current.setRowChildrenExpansion(rowNode.id, !expanded);
        }}
      >
        {expanded ? (
          <KeyboardArrowDown fontSize="small" />
        ) : (
          <KeyboardArrowRight fontSize="small" />
        )}
      </IconButton>
      <Typography variant="subtitle2" noWrap sx={{ minWidth: 0, flexShrink: 1 }}>
        {rollup.name}
      </Typography>
      {/* The count rides with the name instead of holding a column of its own:
          it is one or two digits describing the row it sits on, and a whole
          column of them left a wide, near-empty stripe down the table. */}
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        ({rollup.containerCount})
      </Typography>
      {rollup.issueCount > 0 && (
        <StatusChip
          variant="soft"
          sx={{ flexShrink: 0 }}
          color={rollup.blocked ? "error" : "warning"}
          label={`${rollup.issueCount} ${pluralize("issue", rollup.issueCount)}`}
        />
      )}
    </Stack>
  );
}

/**
 * What the manifest says about this stream, where it differs from the request.
 *
 * The manifest's own figure, in warning colour beside the requested one in the
 * next column — the comparison the page exists to answer, read across a row
 * rather than out of a block above the table. Rows that report a limit on what
 * could be compared rather than a disagreement stay in the notes above: they are
 * a sentence, not a value.
 */
function ManifestDiffCell({
  params,
  rollups,
  diffsByStream,
  documentKind,
}: {
  params: GridRenderCellParams;
  rollups: Map<string, StreamRollup>;
  diffsByStream: Map<string, FieldDiff[]>;
  documentKind?: ShippingDocumentKind | null;
}) {
  if (params.rowNode.type !== "group") {
    return null;
  }
  // Through the rollup rather than off the grouping key: the key carries the
  // name as well, so that sorting orders streams alphabetically, while the
  // comparison is keyed on the stream id alone.
  const streamId = rollups.get(String(params.rowNode.groupingKey))?.streamId;
  const all = (streamId && diffsByStream.get(streamId)) || [];
  const differences = all.filter((diff) => diff.discrepant);

  // Nothing recorded at all is agreement — the comparison only writes a row
  // when it has something to report — and under this header that is what an
  // empty cell says. A row that exists but is not a difference is the other
  // case: the comparison could not be made, which is not the same as no
  // discrepancy and is worth saying out loud.
  if (differences.length === 0) {
    return all.length === 0 ? null : (
      <Typography variant="body2" color="text.secondary" noWrap>
        Not compared
      </Typography>
    );
  }

  return (
    <Tooltip
      title={differences
        .map(
          (diff) =>
            `${diffFieldLabel(diff.field)}: ${formatDiffValue(diff.field, diff.requestValue, diff.unit)} requested, ${formatDiffValue(diff.field, diff.documentValue, diff.unit)} on the ${attachedDocumentNoun(documentKind)}`
        )
        .join(" · ")}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        {/* The colour is the alarm. A severity chip beside it said the same
            thing twice, and made a one-line cell carry two competing marks. */}
        <Typography variant="body2" noWrap sx={{ color: "warning.main", fontWeight: 600 }}>
          {formatDiffValue(differences[0].field, differences[0].documentValue, differences[0].unit)}
        </Typography>
        {differences.length > 1 && (
          <Typography variant="caption" color="text.secondary" noWrap>
            +{differences.length - 1} more
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}

/**
 * Whether the stream is hazardous, in the column beside the codes that say why.
 *
 * A column rather than a chip beside the name: it is a stream fact of exactly
 * the same shape as the three to its right, and inside the name cell it
 * competed with the name for room — a long stream name lost characters to it.
 */
function ClassificationCell({
  params,
  rollups,
}: {
  params: GridRenderCellParams;
  rollups: Map<string, StreamRollup>;
}) {
  if (params.rowNode.type !== "group") {
    return null;
  }
  const classification = rollups.get(String(params.rowNode.groupingKey))?.classification;
  if (!classification) {
    return null;
  }
  // The shared chip rather than a fixed grey one: classification is a hazard
  // level, and the colour is how it reads as one on a screen full of streams.
  return <WasteClassificationChip classification={classification} variant="soft" />;
}

/**
 * One of the stream's classification facts, on the group row that owns it.
 * Blank on the containers underneath, which all share the same value.
 */
function GroupFact({
  params,
  rollups,
  fact,
}: {
  params: GridRenderCellParams;
  rollups: Map<string, StreamRollup>;
  fact: "wasteCodes" | "dotDescription" | "profileNumber";
}) {
  if (params.rowNode.type !== "group") {
    return null;
  }
  const value = rollups.get(String(params.rowNode.groupingKey))?.[fact];
  if (!value) {
    return null;
  }
  return (
    <Box
      component="span"
      sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
    >
      {value}
    </Box>
  );
}

function buildColumns({
  editable,
  contentLockReason,
  shipped,
  rollups,
  diffsByStream,
  documentKind,
  actions,
}: {
  editable: boolean;
  contentLockReason?: string;
  shipped: boolean;
  rollups: Map<string, StreamRollup>;
  diffsByStream?: Map<string, FieldDiff[]>;
  documentKind?: ShippingDocumentKind | null;
  actions: RowActions;
}): CustomGridColDef<DisplayLine>[] {
  const columns: CustomGridColDef<DisplayLine>[] = [
    {
      field: STREAM_GROUPING_FIELD,
      headerName: "Waste Stream",
      // Grouped on, and hidden. Every getter in this file goes through
      // `isDisplayLine` because MUI calls them for auto-generated group rows
      // too, and a group row's `row` is a truthy object carrying only the
      // grouping key — so a plain null check passes and the field access then
      // blows up.
      valueGetter: (_, row) => (isDisplayLine(row) ? streamKeyOf(row) : streamKeyFor(null, null)),
    },
    // These columns describe the STREAM, so they render on its group row and
    // stay blank on the containers beneath it. Repeating them per container
    // restated the same values down the table while leaving the heading that
    // owns them empty.
    {
      field: "classification",
      sortable: false,
      headerName: "Classification",
      flex: 0.7,
      // Enough for "Acutely Hazardous", the longest label, inside a chip —
      // a classification that truncates to "Acutely Hazardo…" is worse than
      // the chip it replaced.
      minWidth: 165,
      renderCell: (params) => <ClassificationCell params={params} rollups={rollups} />,
    },
    {
      field: "wasteCodes",
      sortable: false,
      headerName: "Waste Codes",
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => <GroupFact params={params} rollups={rollups} fact="wasteCodes" />,
    },
    {
      field: "dotDescription",
      sortable: false,
      headerName: "DOT Description",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => <GroupFact params={params} rollups={rollups} fact="dotDescription" />,
    },
    {
      field: "profileNumber",
      sortable: false,
      headerName: "Profile #",
      flex: 0.6,
      minWidth: 90,
      renderCell: (params) => <GroupFact params={params} rollups={rollups} fact="profileNumber" />,
    },
    // Only once there is a manifest to disagree with. A permanently empty
    // column would imply the comparison had been made and found nothing.
    ...(diffsByStream
      ? [
          {
            field: "discrepancies",
            sortable: false,
            // Named for what it reports, which is what lets an empty cell mean
            // "none" rather than "not known". Under a header promising the
            // manifest's figures, a blank would read as missing data — and the
            // comparison writes nothing at all when two sides agree.
            headerName: "Discrepancies",
            flex: 0.9,
            minWidth: 150,
            renderCell: (params: GridRenderCellParams) => (
              <ManifestDiffCell
                params={params}
                rollups={rollups}
                diffsByStream={diffsByStream}
                documentKind={documentKind}
              />
            ),
          } satisfies CustomGridColDef<DisplayLine>,
        ]
      : []),
    {
      field: "amount",
      sortable: false,
      headerName: "Quantity",
      flex: 0.8,
      // Mixed units read as "3.5 gals + 90 lbs", which needs more room than a
      // single figure.
      minWidth: 145,
      renderCell: (params) => <AmountCell params={params} rollups={rollups} shipped={shipped} />,
    },
  ];

  if (editable) {
    columns.push(actionsColumn(contentLockReason, actions));
  }

  return columns;
}

/**
 * The per-container actions, behind a kebab.
 *
 * A disabled item states why in its own label rather than through a tooltip:
 * MUI's menu items swallow pointer events when disabled, so a tooltip on one
 * never opens.
 */
function actionsColumn(
  contentLockReason: string | undefined,
  actions: RowActions
): CustomGridColDef<DisplayLine> {
  const locked = Boolean(contentLockReason);
  return {
    field: "actions",
    type: "actions",
    headerName: "",
    width: 56,
    getActions: (params) => {
      // A group row has nothing to act on; the actions belong to containers.
      const line = params.row;
      if (!isDisplayLine(line)) {
        return [];
      }
      if (line.pending === "removed") {
        return [
          <GridActionsCellItem
            key="restore"
            showInMenu
            icon={<Undo fontSize="small" />}
            label="Undo removal"
            onClick={() => actions.onRestoreLine(line)}
          />,
        ];
      }
      // A staged container has no saved line to validate a partial amount
      // against, so editing its quantity waits for the save.
      const editBlockedReason =
        contentLockReason ??
        (line.lineId ? undefined : "Save the shipment before shipping part of this container");
      return [
        <GridActionsCellItem
          key="edit"
          showInMenu
          icon={<Edit fontSize="small" />}
          label={editBlockedReason ?? "Edit quantity"}
          disabled={Boolean(editBlockedReason)}
          onClick={() => actions.onEditLine(line)}
        />,
        <GridActionsCellItem
          key="remove"
          showInMenu
          icon={<Delete fontSize="small" />}
          label={contentLockReason ?? "Remove from shipment"}
          disabled={locked}
          onClick={() => actions.onRemoveLine(line)}
        />,
      ];
    },
  };
}
